import React, { useState, useEffect, useRef } from 'react';
import {
  Image, Plus, Edit, Trash2, Eye, EyeOff, ArrowLeft, Upload, GripVertical,
  Settings, Play, Pause, ChevronUp, ChevronDown, Link2, Type, AlignLeft,
  AlignCenter, AlignRight, Palette, Clock, RefreshCw, Shield, Search,
  ExternalLink, Save, X, Smartphone, Monitor, Copy
} from 'lucide-react';
import { heroCarousel, storage } from '../lib/supabase';

// Available icons for buttons
const BUTTON_ICONS = [
  { id: 'Shield', label: 'Shield', component: Shield },
  { id: 'Search', label: 'Search', component: Search },
  { id: 'ExternalLink', label: 'External Link', component: ExternalLink },
  { id: null, label: 'No Icon', component: null }
];

// Transition styles
const TRANSITION_STYLES = [
  { id: 'fade', label: 'Fade', description: 'Smooth opacity transition' },
  { id: 'slide', label: 'Slide', description: 'Horizontal sliding motion' },
  { id: 'zoom', label: 'Zoom', description: 'Scale in/out effect' },
  { id: 'flip', label: 'Flip', description: '3D flip rotation' }
];

// Gradient presets
const GRADIENT_PRESETS = [
  { id: 'from-emerald-900 via-emerald-800 to-emerald-900', label: 'Emerald (Default)' },
  { id: 'from-blue-900 via-blue-800 to-blue-900', label: 'Blue' },
  { id: 'from-violet-900 via-violet-800 to-violet-900', label: 'Violet' },
  { id: 'from-slate-900 via-slate-800 to-slate-900', label: 'Slate' },
  { id: 'from-amber-900 via-amber-800 to-amber-900', label: 'Amber' },
  { id: 'from-rose-900 via-rose-800 to-rose-900', label: 'Rose' }
];

// ==================== ADMIN HERO CAROUSEL PAGE ====================
export const AdminHeroCarouselPage = ({ setPage }) => {
  const [slides, setSlides] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null, 'new', or slide id
  const [editingSettings, setEditingSettings] = useState(false);
  const [formData, setFormData] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewMobile, setPreviewMobile] = useState(null);
  const [activeTab, setActiveTab] = useState('slides'); // 'slides' | 'settings'
  const fileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [slidesRes, settingsRes] = await Promise.all([
      heroCarousel.getAllSlides(),
      heroCarousel.getSettings()
    ]);
    setSlides(slidesRes.data || []);
    setSettings(settingsRes.data || {
      autoplay: true,
      autoplay_interval: 5000,
      transition_style: 'fade',
      transition_duration: 500,
      pause_on_hover: true,
      show_indicators: true,
      show_arrows: true,
      loop: true
    });
    setLoading(false);
  };

  // File upload handler
  const handleFileUpload = async (e, isMobile = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (event) => {
      if (isMobile) {
        setPreviewMobile(event.target.result);
      } else {
        setPreviewImage(event.target.result);
      }
    };
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `hero/${Date.now()}_${isMobile ? 'mobile' : 'desktop'}.${ext}`;
      const { error } = await storage.uploadImage('hero-images', path, file);

      if (!error) {
        const url = storage.getPublicUrl('hero-images', path);
        setFormData({
          ...formData,
          [isMobile ? 'background_image_url_mobile' : 'background_image_url']: url
        });
      } else {
        console.error('Upload error:', error);
        alert('Error uploading image. Make sure the hero-images bucket exists.');
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Save slide
  const handleSaveSlide = async () => {
    try {
      const slideData = {
        ...formData,
        buttons: formData.buttons || []
      };

      const { error } = editing === 'new'
        ? await heroCarousel.create(slideData)
        : await heroCarousel.update(editing, slideData);
      if (error) throw error;
      await loadData();
      setEditing(null);
      setFormData({});
      setPreviewImage(null);
      setPreviewMobile(null);
    } catch (error) {
      console.error('Error saving slide:', error);
      alert('Error saving slide: ' + error.message);
    }
  };

  // Save settings
  const [saveMessage, setSaveMessage] = useState(null);

  const handleSaveSettings = async () => {
    try {
      const { data, error } = await heroCarousel.updateSettings(settingsForm);
      if (error) {
        console.error('Error saving settings:', error);
        setSaveMessage({ type: 'error', text: 'Error saving settings: ' + error.message });
        setTimeout(() => setSaveMessage(null), 3000);
        return;
      }
      setSettings(settingsForm);
      setEditingSettings(false);
      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({ type: 'error', text: 'Error saving settings: ' + error.message });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Delete slide
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this slide? This cannot be undone.')) return;
    try {
      const { error } = await heroCarousel.delete(id);
      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error deleting slide: ' + error.message);
    }
  };

  // Duplicate slide
  const handleDuplicate = async (slide) => {
    try {
      const { id, created_at, updated_at, ...slideData } = slide;
      const duplicatedSlide = {
        ...slideData,
        heading: `${slide.heading} (Copy)`,
        sort_order: slides.length, // Add to end
        is_active: false // Start as inactive
      };
      const { error } = await heroCarousel.create(duplicatedSlide);
      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error duplicating:', error);
      alert('Error duplicating slide: ' + error.message);
    }
  };

  // Toggle active
  const handleToggleActive = async (slide) => {
    try {
      const { error } = await heroCarousel.toggleActive(slide.id, !slide.is_active);
      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error toggling:', error);
      alert('Error toggling slide: ' + error.message);
    }
  };

  // Move slide up/down
  const handleMove = async (index, direction) => {
    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSlides.length) return;

    [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
    const slideIds = newSlides.map(s => s.id);
    const { error } = await heroCarousel.reorder(slideIds);
    if (error) {
      console.error('Error reordering:', error);
      alert('Error reordering slides: ' + error.message);
      return;
    }
    await loadData();
  };

  // Start editing new slide
  const startNew = () => {
    setEditing('new');
    setFormData({
      heading: '',
      heading_highlight: '',
      subtext: '',
      background_type: 'gradient',
      background_image_url: '',
      background_image_url_mobile: '',
      background_gradient: 'from-emerald-900 via-emerald-800 to-emerald-900',
      background_overlay_opacity: 0.3,
      buttons: [],
      text_alignment: 'left',
      text_color: 'white',
      accent_color: 'yellow-400',
      is_active: true
    });
    setPreviewImage(null);
    setPreviewMobile(null);
  };

  // Start editing existing slide
  const startEdit = (slide) => {
    setEditing(slide.id);
    setFormData({
      ...slide,
      buttons: slide.buttons || []
    });
    setPreviewImage(slide.background_image_url);
    setPreviewMobile(slide.background_image_url_mobile);
  };

  // Add button to slide
  const addButton = () => {
    const buttons = formData.buttons || [];
    setFormData({
      ...formData,
      buttons: [...buttons, { text: 'Button', link: '', linkType: 'internal', style: 'primary', icon: null }]
    });
  };

  // Update button
  const updateButton = (index, field, value) => {
    const buttons = [...(formData.buttons || [])];
    buttons[index] = { ...buttons[index], [field]: value };
    setFormData({ ...formData, buttons });
  };

  // Remove button
  const removeButton = (index) => {
    const buttons = [...(formData.buttons || [])];
    buttons.splice(index, 1);
    setFormData({ ...formData, buttons });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setPage('admin')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hero Carousel</h1>
            <p className="text-gray-500">Manage homepage hero slides and settings</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setActiveTab('settings'); setEditingSettings(true); setSettingsForm(settings); }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            <Settings className="w-4 h-4" /> Settings
          </button>
          <button
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
          >
            <Plus className="w-4 h-4" /> Add Slide
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('slides')}
          className={`px-4 py-2 border-b-2 ${activeTab === 'slides' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Slides ({slides.length})
        </button>
        <button
          onClick={() => { setActiveTab('settings'); setSettingsForm(settings); }}
          className={`px-4 py-2 border-b-2 ${activeTab === 'settings' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Carousel Settings
        </button>
      </div>

      {/* Content */}
      {activeTab === 'slides' && !editing && (
        <div className="space-y-4">
          {slides.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No slides yet. Add your first slide to get started.</p>
              <button onClick={startNew} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                <Plus className="w-4 h-4 inline mr-2" /> Add First Slide
              </button>
            </div>
          ) : (
            slides.map((slide, index) => (
              <div key={slide.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${!slide.is_active ? 'opacity-60' : ''}`}>
                <div className="flex">
                  {/* Preview */}
                  <div className="w-72 h-40 bg-gray-100 flex-shrink-0 relative overflow-hidden">
                    {slide.background_type === 'image' && slide.background_image_url ? (
                      <img src={slide.background_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br ${slide.background_gradient || 'from-emerald-900 to-emerald-800'}`} />
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center p-4">
                      <div className="text-white text-center">
                        <p className="font-bold text-sm line-clamp-2">{slide.heading}</p>
                        {slide.heading_highlight && (
                          <p className="text-yellow-400 text-xs">{slide.heading_highlight}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex-1 p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">{slide.heading}</h3>
                        {slide.heading_highlight && (
                          <span className="text-yellow-600 font-medium">{slide.heading_highlight}</span>
                        )}
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{slide.subtext}</p>
                        <div className="flex gap-2 mt-2">
                          {(slide.buttons || []).map((btn, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-gray-100 rounded">
                              {btn.text}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMove(index, 'up')}
                          disabled={index === 0}
                          className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMove(index, 'down')}
                          disabled={index === slides.length - 1}
                          className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-3 border-t">
                      <div className="flex gap-2 text-xs text-gray-500">
                        <span className={`px-2 py-0.5 rounded ${slide.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {slide.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 rounded capitalize">
                          {slide.background_type}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleActive(slide)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title={slide.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {slide.is_active ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-green-600" />}
                        </button>
                        <button
                          onClick={() => handleDuplicate(slide)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="Duplicate slide"
                        >
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => startEdit(slide)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="Edit slide"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(slide.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="Delete slide"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Edit/Create Slide Form */}
      {editing && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold">{editing === 'new' ? 'Create New Slide' : 'Edit Slide'}</h2>
            <button onClick={() => { setEditing(null); setFormData({}); }} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Content */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Type className="w-4 h-4" /> Content
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heading *</label>
                <input
                  type="text"
                  value={formData.heading || ''}
                  onChange={(e) => setFormData({ ...formData, heading: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="Know Yuh Rights"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heading Highlight (Accent Color)</label>
                <input
                  type="text"
                  value={formData.heading_highlight || ''}
                  onChange={(e) => setFormData({ ...formData, heading_highlight: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="& So Much More"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtext</label>
                <textarea
                  value={formData.subtext || ''}
                  onChange={(e) => setFormData({ ...formData, subtext: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  rows={2}
                  placeholder="Road Traffic Regulations, 2022..."
                />
              </div>

              {/* Buttons */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Buttons</label>
                  <button
                    onClick={addButton}
                    className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add Button
                  </button>
                </div>
                {(formData.buttons || []).map((btn, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 mb-2">
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={btn.text || ''}
                        onChange={(e) => updateButton(index, 'text', e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border rounded"
                        placeholder="Button Text"
                      />
                      <button
                        onClick={() => removeButton(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={btn.linkType || 'internal'}
                        onChange={(e) => updateButton(index, 'linkType', e.target.value)}
                        className="px-2 py-1 text-sm border rounded"
                      >
                        <option value="internal">Internal Page</option>
                        <option value="external">External URL</option>
                      </select>
                      <input
                        type="text"
                        value={btn.link || ''}
                        onChange={(e) => updateButton(index, 'link', e.target.value)}
                        className="px-2 py-1 text-sm border rounded"
                        placeholder={btn.linkType === 'external' ? 'https://...' : 'walkthrough'}
                      />
                      <select
                        value={btn.style || 'primary'}
                        onChange={(e) => updateButton(index, 'style', e.target.value)}
                        className="px-2 py-1 text-sm border rounded"
                      >
                        <option value="primary">Primary (Yellow)</option>
                        <option value="secondary">Secondary (Outline)</option>
                      </select>
                      <select
                        value={btn.icon || ''}
                        onChange={(e) => updateButton(index, 'icon', e.target.value || null)}
                        className="px-2 py-1 text-sm border rounded"
                      >
                        <option value="">No Icon</option>
                        <option value="Shield">Shield</option>
                        <option value="Search">Search</option>
                        <option value="ExternalLink">External Link</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Text Alignment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Alignment</label>
                <div className="flex gap-2">
                  {[
                    { id: 'left', icon: AlignLeft },
                    { id: 'center', icon: AlignCenter },
                    { id: 'right', icon: AlignRight }
                  ].map(({ id, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setFormData({ ...formData, text_alignment: id })}
                      className={`p-2 rounded-lg border ${formData.text_alignment === id ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-gray-50'}`}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Background */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Image className="w-4 h-4" /> Background
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFormData({ ...formData, background_type: 'gradient' })}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${formData.background_type === 'gradient' ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-gray-50'}`}
                  >
                    <Palette className="w-4 h-4 inline mr-2" /> Gradient
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, background_type: 'image' })}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${formData.background_type === 'image' ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-gray-50'}`}
                  >
                    <Image className="w-4 h-4 inline mr-2" /> Image
                  </button>
                </div>
              </div>

              {formData.background_type === 'gradient' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gradient Preset</label>
                  <div className="grid grid-cols-2 gap-2">
                    {GRADIENT_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => setFormData({ ...formData, background_gradient: preset.id })}
                        className={`p-2 rounded-lg border text-left ${formData.background_gradient === preset.id ? 'ring-2 ring-emerald-500' : ''}`}
                      >
                        <div className={`h-8 rounded bg-gradient-to-r ${preset.id} mb-1`} />
                        <span className="text-xs text-gray-600">{preset.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {formData.background_type === 'image' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Monitor className="w-4 h-4 inline mr-1" /> Desktop Image
                    </label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      {previewImage ? (
                        <div className="relative">
                          <img src={previewImage} alt="" className="max-h-40 max-w-full mx-auto rounded object-contain" />
                          <button
                            onClick={() => { setPreviewImage(null); setFormData({ ...formData, background_image_url: '' }); }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 mb-2">Recommended: 1920x600px</p>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) => handleFileUpload(e, false)}
                            accept="image/*"
                            className="hidden"
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                          >
                            {uploading ? 'Uploading...' : 'Choose File'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Smartphone className="w-4 h-4 inline mr-1" /> Mobile Image (Optional)
                    </label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      {previewMobile ? (
                        <div className="relative">
                          <img src={previewMobile} alt="" className="max-h-32 max-w-full mx-auto rounded object-contain" />
                          <button
                            onClick={() => { setPreviewMobile(null); setFormData({ ...formData, background_image_url_mobile: '' }); }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs text-gray-500 mb-2">Recommended: 768x400px</p>
                          <input
                            type="file"
                            ref={mobileFileInputRef}
                            onChange={(e) => handleFileUpload(e, true)}
                            accept="image/*"
                            className="hidden"
                          />
                          <button
                            onClick={() => mobileFileInputRef.current?.click()}
                            disabled={uploading}
                            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          >
                            {uploading ? 'Uploading...' : 'Choose File'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Overlay Opacity: {Math.round((formData.background_overlay_opacity || 0.3) * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={formData.background_overlay_opacity || 0.3}
                      onChange={(e) => setFormData({ ...formData, background_overlay_opacity: parseFloat(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                </>
              )}

              {/* Active Status */}
              <div className="flex items-center gap-3 pt-4 border-t">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active !== false}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
                <span className="text-sm font-medium text-gray-700">Active</span>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-semibold text-gray-700 mb-3">Preview</h3>
            <div
              className={`relative overflow-hidden rounded-xl p-8 text-white ${
                formData.background_type === 'image' ? '' : `bg-gradient-to-br ${formData.background_gradient || 'from-emerald-900 to-emerald-800'}`
              }`}
              style={formData.background_type === 'image' && previewImage ? {
                backgroundImage: `url(${previewImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              } : {}}
            >
              {formData.background_type === 'image' && (
                <div
                  className="absolute inset-0 bg-black"
                  style={{ opacity: formData.background_overlay_opacity || 0.3 }}
                />
              )}
              <div className={`relative z-10 ${formData.text_alignment === 'center' ? 'text-center' : formData.text_alignment === 'right' ? 'text-right' : ''}`}>
                <h2 className="text-3xl font-black mb-2">
                  {formData.heading || 'Your Heading Here'}
                  {formData.heading_highlight && (
                    <><br /><span className="text-yellow-400">{formData.heading_highlight}</span></>
                  )}
                </h2>
                <p className="text-white/80 mb-4 max-w-xl">{formData.subtext || 'Your subtext will appear here...'}</p>
                <div className={`flex gap-3 ${formData.text_alignment === 'center' ? 'justify-center' : formData.text_alignment === 'right' ? 'justify-end' : ''}`}>
                  {(formData.buttons || []).map((btn, i) => (
                    <button
                      key={i}
                      className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
                        btn.style === 'primary'
                          ? 'bg-yellow-500 text-emerald-900'
                          : 'bg-white/10 border border-white/30 text-white'
                      }`}
                    >
                      {btn.icon === 'Shield' && <Shield className="w-4 h-4" />}
                      {btn.icon === 'Search' && <Search className="w-4 h-4" />}
                      {btn.icon === 'ExternalLink' && <ExternalLink className="w-4 h-4" />}
                      {btn.text || 'Button'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => { setEditing(null); setFormData({}); }}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSlide}
              disabled={!formData.heading}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save Slide
            </button>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
            <Settings className="w-5 h-5" /> Carousel Settings
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Playback */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700">Playback</h3>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Autoplay</label>
                  <p className="text-sm text-gray-500">Automatically advance slides</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.autoplay !== false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, autoplay: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" /> Slide Duration: {(settingsForm.autoplay_interval || 5000) / 1000}s
                </label>
                <input
                  type="range"
                  min="2000"
                  max="15000"
                  step="1000"
                  value={settingsForm.autoplay_interval || 5000}
                  onChange={(e) => setSettingsForm({ ...settingsForm, autoplay_interval: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>2s</span>
                  <span>15s</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Pause on Hover</label>
                  <p className="text-sm text-gray-500">Stop autoplay when user hovers</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.pause_on_hover !== false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, pause_on_hover: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Loop</label>
                  <p className="text-sm text-gray-500">Restart from first slide after last</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.loop !== false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, loop: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>
            </div>

            {/* Appearance */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700">Appearance</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transition Style</label>
                <div className="grid grid-cols-2 gap-2">
                  {TRANSITION_STYLES.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setSettingsForm({ ...settingsForm, transition_style: style.id })}
                      className={`p-3 rounded-lg border text-left ${settingsForm.transition_style === style.id ? 'bg-emerald-50 border-emerald-500' : 'hover:bg-gray-50'}`}
                    >
                      <span className="font-medium">{style.label}</span>
                      <p className="text-xs text-gray-500">{style.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transition Duration: {(settingsForm.transition_duration || 500)}ms
                </label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={settingsForm.transition_duration || 500}
                  onChange={(e) => setSettingsForm({ ...settingsForm, transition_duration: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Fast (200ms)</span>
                  <span>Slow (1.5s)</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Show Indicators</label>
                  <p className="text-sm text-gray-500">Dots showing current slide</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.show_indicators !== false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, show_indicators: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Show Arrows</label>
                  <p className="text-sm text-gray-500">Previous/Next navigation arrows</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.show_arrows !== false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, show_arrows: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              onClick={() => {
                setSettingsForm(settings);
                setSaveMessage({ type: 'success', text: 'Settings reset to saved values' });
                setTimeout(() => setSaveMessage(null), 2000);
              }}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              onClick={handleSaveSettings}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save Settings
            </button>
          </div>
          {saveMessage && (
            <div className={`mt-4 p-3 rounded-lg ${saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {saveMessage.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminHeroCarouselPage;
